import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import TextareaOnboarding from "components/onboarding/TextareaOnboarding";
import { useMutation } from "@tanstack/react-query";
import { useOnboardingPromotionPage } from "../../../../hooks/useOnboardingPromotionPage";
import { BottomNextButton } from "../../../../components/common/BottomNextButton";
import useMixpanelProfileMkt from "../../../../hooks/useMixpanelProfileMkt";
import useMixpanelMKT from "../../../../hooks/useMixpanelMKT";
import { webFunnelApi } from "../../../../api/webFunnelApi";
import { captureException } from "../../../../utils/sentry";
import {
  MktVersionTypes,
  versionToPathPrefix,
} from "../../../../components/mkt/mktVersionUtils";
import mixpanel from "mixpanel-browser";
import { OnboardingDemographicType } from "../../../../api/onboardingApi";

export default function Demographic_3_V6({
  version = "v6",
}: {
  version?: MktVersionTypes;
}) {
  /* ➊ HTMLTextAreaElement 참조용 useRef 생성 */
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const pageKey = "demographic_3";

  const {
    text: age,
    updateText: updateAge,
    handleNext,
  } = useOnboardingPromotionPage({
    pageKey,
    nextPageRoute: `/${versionToPathPrefix(version)}-onboarding/demographic/4`,
  });

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [loading] = useState<boolean>(false);

  const isButtonDisabled =
    age === undefined || age === "" || Number(age) <= 7 || Number(age) >= 100;

  const trackPromotion = useMixpanelMKT({
    eventName: "나이 제출",
    params: {
      age: age,
    },
    version: version,
  });

  const setMixpanelProfileMkt = useMixpanelProfileMkt(version);

  // Session storage에서 데이터 복원
  useEffect(() => {
    const storedData = sessionStorage.getItem("demographic_data");
    if (storedData) {
      try {
        const demographicData: OnboardingDemographicType =
          JSON.parse(storedData);
        if (demographicData.age) {
          updateAge(demographicData.age.toString());
        }
      } catch (error) {
        console.error("Failed to parse demographic data:", error);
      }
    }
  }, []);

  const { mutate: submitAge } = useMutation(
    ({ distinctId, age }: { distinctId: string; age: number }) =>
      webFunnelApi.submitAge(distinctId, age),
    {
      onError: (error, variables) => {
        captureException(error, {
          tags: { action: "web_funnel_submit_age" },
          extra: { pageKey, age: variables?.age },
        });
      },
    }
  );

  function handleSubmit() {
    if (age && age.length > 0 && Number(age) > 7) {
      trackPromotion();

      setMixpanelProfileMkt({ 나이: Number(age) });

      const distinctId =
        localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ||
        (mixpanel as any).promotion?.get_distinct_id();

      if (distinctId) {
        submitAge({ distinctId, age: Number(age) });
      } else {
        console.warn(
          `[${pageKey}] WEB_FUNNEL_DISTINCT_ID를 찾지 못해 age submit을 건너뜁니다.`
        );
      }

      handleNext({ text: age });
    }
  }

  /* ➋ 페이지 로드 후 자동으로 텍스트에어리어에 포커스 */
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  return (
    <Stack spacing={5} sx={{ height: "100%" }}>
      <Stack spacing={2}>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {
            "인생의 어느 시기를 지나고 있느냐에 따라 마음속 중요한 주제들이 변화하기도 한답니다."
          }
        </CustomTypography>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {"나이는 어떻게 되나요?"}
        </CustomTypography>
      </Stack>

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={1}
        sx={{ width: "100%" }}
      >
        <Typography
          variant="Body"
          fontWeight={500}
          sx={{
            color: theme.vars.palette.Content.Strong,
          }}
        >
          만
        </Typography>
        <Box
          sx={{
            width: "100px",
            height: "54px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            boxShadow: isFocused
              ? `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`
              : `inset 0 0 0 0`,
          }}
        >
          <TextareaOnboarding
            initialValue={age}
            handleChange={(newValue: string) => {
              updateAge(newValue);

              // Session storage 업데이트
              const storedData = sessionStorage.getItem("demographic_data");
              const demographicData: OnboardingDemographicType = storedData
                ? JSON.parse(storedData)
                : {};
              demographicData.age = newValue ? Number(newValue) : undefined;
              sessionStorage.setItem(
                "demographic_data",
                JSON.stringify(demographicData)
              );
            }}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            emojiDisabled={true}
            readOnly={false}
            textAlign={"center"}
            numbersOnly={true}
            maxLength={2}
            /* ➌ 여기서 자식(TextareaOnboarding)에 ref를 넘김 */
            // inputRef={textAreaRef}
          />
        </Box>
        <Typography
          variant="Body"
          fontWeight={500}
          sx={{
            color: theme.vars.palette.Content.Strong,
          }}
        >
          세
        </Typography>
      </Stack>

      <BottomNextButton
        loading={loading}
        disabled={isButtonDisabled}
        onClick={handleSubmit}
        text={"다음"}
        highlightBackground={false}
      />
    </Stack>
  );
}
